import React, { useEffect, useRef, useState } from "react";
import SidebarItem, { SidebarItemProps } from "./SidebarItem";
import { useLocation } from "react-router";
import styles from "./Sidebar.module.scss";
import cn from "classnames";
import { readObj, write } from "Service/storage";
import { KeyboardArrowDownRounded } from "@mui/icons-material";
import SidebarDisclosure from "./SidebarDisclosure";

type SidebarHeaderItemProps = {
  title: string;
  icon: React.ElementType;
  subMenu: SidebarItemProps[];
  collapsed: boolean;
  searching: boolean;
};

const checkIsPageActive = (list: SidebarItemProps[], path: string) =>
  list.some(
    (item) =>
      path === item.url ||
      path.startsWith(item.url + "/") ||
      item.subMenu?.some(
        (elem) =>
          elem.url && (path === elem.url || path.startsWith(elem.url + "/")),
      ),
  );

const SidebarHeaderItem: React.FC<SidebarHeaderItemProps> = ({
  title,
  icon,
  subMenu,
  collapsed,
  searching,
}) => {
  const openedSections = (readObj("openedSections") || []) as string[];
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(
    checkIsPageActive(subMenu, location.pathname) ||
      openedSections.includes(title),
  );
  const sectionRef = useRef<HTMLDivElement>(null);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const visible = collapsed ? flyoutOpen : searching || isOpen;

  const toggleSection = () => {
    if (collapsed) {
      setFlyoutOpen((current) => !current);
      return;
    }
    const next = !isOpen;
    setIsOpen(next);
    if (!collapsed) {
      const currentOpenedSections = (readObj("openedSections") ||
        []) as string[];
      write(
        "openedSections",
        next
          ? [...new Set([...currentOpenedSections, title])]
          : currentOpenedSections.filter((item) => item !== title),
      );
    }
  };

  useEffect(() => {
    if (checkIsPageActive(subMenu, location.pathname)) {
      setIsOpen(true);
    }
  }, [subMenu, location]);

  useEffect(() => {
    const currentOpenedSections = readObj("openedSections") || [];
    if (isOpen && !currentOpenedSections.includes(title)) {
      write("openedSections", [...currentOpenedSections, title]);
    }
  }, [title]);

  useEffect(() => {
    setFlyoutOpen(false);
  }, [collapsed, location.pathname]);

  useEffect(() => {
    if (!collapsed || !flyoutOpen) return;
    const closeOutside = (event: MouseEvent) => {
      if (!sectionRef.current?.contains(event.target as Node))
        setFlyoutOpen(false);
    };
    const closeEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setFlyoutOpen(false);
        sectionRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
      }
    };
    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("keydown", closeEscape);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      document.removeEventListener("keydown", closeEscape);
    };
  }, [collapsed, flyoutOpen]);

  return (
    <div
      ref={sectionRef}
      onBlur={(event) => {
        if (
          collapsed &&
          !event.currentTarget.contains(event.relatedTarget as Node | null)
        )
          setFlyoutOpen(false);
      }}
      className={cn(
        styles.sidebar__section,
        visible && styles.sidebar__section_open,
        checkIsPageActive(subMenu, location.pathname) &&
          styles.sidebar__section_active,
      )}
    >
      <button
        type="button"
        aria-label={collapsed ? title : undefined}
        aria-expanded={visible}
        title={collapsed ? title : undefined}
        onClick={toggleSection}
        className={styles.sidebar__header}
      >
        <div className={styles.sidebar__header_icon}>
          {React.createElement(icon, { "aria-hidden": true })}
        </div>
        {!collapsed && (
          <span className={styles.sidebar__header_title}>{title}</span>
        )}
        {!collapsed && (
          <KeyboardArrowDownRounded
            className={styles.sidebar__chevron}
            aria-hidden="true"
          />
        )}
      </button>

      <SidebarDisclosure open={visible} className={styles.sidebar__mainList}>
        {collapsed && (
          <div className={styles.sidebar__flyoutTitle}>{title}</div>
        )}
        {subMenu.map((item) => (
          <SidebarItem key={item.title} {...item} searching={searching} />
        ))}
      </SidebarDisclosure>
    </div>
  );
};

export default SidebarHeaderItem;
